import type { Deal } from '../types';

export const AGE_HIGH_THRESHOLD = 90;
export const AGE_MEDIUM_THRESHOLD = 45;

// The backend can return multiple rows for the same deal (e.g. one row per product
// line) — dedupe by id before counting or summing, keeping the first occurrence.
export function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter(item => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

export function sumUniqueBy(deals: Deal[], getValue: (d: Deal) => number): number {
  return dedupeById(deals).reduce((sum, d) => sum + (getValue(d) || 0), 0);
}

// age is null when entry/completion is missing or unparseable — exclude from stats.
export function validAges(deals: Deal[]): number[] {
  return deals.map(d => d.age).filter((a): a is number => a !== null);
}

// sc can be a comma-separated list when a request has multiple assignees (see
// json-array-join in the backend report config) — each name is its own SC for
// grouping/counting purposes, not one combined key.
export function splitSCNames(sc: string | null | undefined): string[] {
  if (!sc) return ['No Value'];
  const names = sc.split(',').map(s => s.trim()).filter(Boolean);
  return names.length > 0 ? names : ['No Value'];
}

export function isAged(deal: Deal, threshold: number = AGE_HIGH_THRESHOLD): boolean {
  return deal.age !== null && deal.age > threshold;
}

export type AgeSeverity = 'high' | 'medium' | 'none';

export function ageSeverity(age: number | null): AgeSeverity {
  if (age === null) return 'none';
  if (age > AGE_HIGH_THRESHOLD) return 'high';
  if (age > AGE_MEDIUM_THRESHOLD) return 'medium';
  return 'none';
}

export function formatAge(age: number | null): string {
  return age !== null ? `${age}d` : '—';
}

// Descending by age; null ages (unknown) sort last.
export function compareAgeDesc(a: Deal, b: Deal): number {
  return (b.age ?? -1) - (a.age ?? -1);
}

// Descending by age; null ages (unknown) sort last.
export function getURL(d: Deal): string {
  return  (d.requestID !== null ? `https://experience.adobe.com/#/@wfadoberm/so:adoberm-Production/workfront/issue/${d.requestID}` : '#');
}
